import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useBusinessBrain } from '../../context/BusinessBrainContext';
import { toast } from 'sonner';
import { FiX, FiCheck, FiCpu, FiUser, FiZap } from 'react-icons/fi';

const GnomeSettingsModal = ({ gnome, isOpen, onClose }) => {
  const { activeWorkspace } = useWorkspace();
  const { brainData } = useBusinessBrain();
  const [mode, setMode] = useState('co-pilot');
  const [guardrails, setGuardrails] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // AI Generator State
  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (isOpen && activeWorkspace && gnome) {
      fetchSettings();
    }
  }, [isOpen, activeWorkspace, gnome]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('gnome_settings')
        .select('*')
        .eq('workspace_id', activeWorkspace.id)
        .eq('gnome_id', gnome.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setMode(data.mode);
        setGuardrails(data.guardrails || '');
      } else {
        // Defaults
        setMode('co-pilot');
        setGuardrails('');
      }
    } catch (error) {
      console.error('Error fetching gnome settings:', error);
      toast.error('Failed to load settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateGuardrails = async () => {
    if (!brainData) {
      toast.error("Business Brain data not available.");
      return;
    }
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-gnome-guardrails', {
        body: {
          gnome: { title: gnome.title, id: gnome.id, role: gnome.role },
          userPrompt: aiPrompt,
          brainData
        }
      });

      if (error) throw error;
      if (data && data.guardrails) {
        setGuardrails(data.guardrails);
        toast.success("Guardrails generated!");
        setShowAiPrompt(false);
      }
    } catch (err) {
      console.error("Error generating guardrails:", err);
      toast.error("Failed to generate guardrails.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!activeWorkspace || !gnome) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('gnome_settings')
        .upsert({
          workspace_id: activeWorkspace.id,
          gnome_id: gnome.id,
          mode,
          guardrails,
          updated_at: new Date()
        }, { onConflict: 'workspace_id, gnome_id' });

      if (error) throw error;

      toast.success(`${gnome.title} settings updated!`);
      onClose();
    } catch (error) {
      console.error('Error saving gnome settings:', error);
      toast.error('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[50000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white dark:bg-charcoal-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-charcoal-200 dark:border-charcoal-700 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className={`p-4 border-b border-charcoal-100 dark:border-charcoal-700 flex justify-between items-center ${gnome.color} text-white`}>
          <h3 className="font-bold text-lg flex items-center gap-2">
            Settings: {gnome.title}
          </h3>
          <button onClick={onClose} aria-label="Close Settings" className="p-1 hover:bg-white/20 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50">
            <FiX size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 p-6 overflow-y-auto space-y-6">
          {loading ? (
            <div className="text-center py-8 text-charcoal-400">Loading settings...</div>
          ) : (
            <>
              {/* Mode Selection */}
              <div>
                <label className="block text-sm font-bold text-charcoal-700 dark:text-charcoal-300 mb-3">
                  Operation Mode
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setMode('co-pilot')}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 text-center ${
                      mode === 'co-pilot'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'border-charcoal-200 dark:border-charcoal-700 hover:border-charcoal-300 dark:hover:border-charcoal-600 text-charcoal-500'
                    }`}
                    aria-pressed={mode === 'co-pilot'}
                  >
                    <FiUser size={24} className={mode === 'co-pilot' ? 'text-blue-500' : ''} />
                    <span className="font-bold text-sm">Co-Pilot</span>
                    <span className="text-xs opacity-70">You approve every action.</span>
                  </button>

                  <button
                    onClick={() => setMode('auto-pilot')}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 text-center ${
                      mode === 'auto-pilot'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                        : 'border-charcoal-200 dark:border-charcoal-700 hover:border-charcoal-300 dark:hover:border-charcoal-600 text-charcoal-500'
                    }`}
                    aria-pressed={mode === 'auto-pilot'}
                  >
                    <FiCpu size={24} className={mode === 'auto-pilot' ? 'text-purple-500' : ''} />
                    <span className="font-bold text-sm">Autopilot</span>
                    <span className="text-xs opacity-70">Runs freely within guardrails.</span>
                  </button>
                </div>
              </div>

              {/* Guardrails */}
              <div>
                <div className="flex justify-between items-end mb-2">
                  <label className="block text-sm font-bold text-charcoal-700 dark:text-charcoal-300">
                    Guardrails & Guidelines
                  </label>
                  <button
                    onClick={() => setShowAiPrompt(!showAiPrompt)}
                    className="text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 rounded px-1"
                    aria-expanded={showAiPrompt}
                  >
                    <FiZap aria-hidden="true" /> {showAiPrompt ? 'Hide Generator' : 'Generate with AI'}
                  </button>
                </div>

                {showAiPrompt && (
                  <div className="mb-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-100 dark:border-purple-800/50 animate-in fade-in slide-in-from-top-2">
                     <label className="block text-xs font-bold text-purple-800 dark:text-purple-300 mb-1">
                        What kind of rules do you need?
                     </label>
                     <div className="flex gap-2">
                        <input
                           type="text"
                           value={aiPrompt}
                           onChange={(e) => setAiPrompt(e.target.value)}
                           placeholder="E.g., Strictly formal tone, never mention competitors..."
                           className="flex-1 px-3 py-2 rounded-lg text-sm border border-purple-200 dark:border-purple-700 bg-white dark:bg-charcoal-900 focus:ring-2 focus:ring-purple-500 outline-none"
                           onKeyDown={(e) => e.key === 'Enter' && !isGenerating && handleGenerateGuardrails()}
                        />
                        <button
                           onClick={handleGenerateGuardrails}
                           disabled={isGenerating}
                           className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-bold disabled:opacity-50 transition-colors flex items-center gap-2"
                        >
                           {isGenerating ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <FiZap />}
                           Generate
                        </button>
                     </div>
                  </div>
                )}

                <p className="text-xs text-charcoal-500 dark:text-charcoal-400 mb-3">
                  Define strict rules for {gnome.title} to follow. E.g., "Never mention competitors", "Keep tone strictly professional", "Max budget $50/day".
                </p>
                <textarea
                  value={guardrails}
                  onChange={(e) => setGuardrails(e.target.value)}
                  placeholder={`Enter specific rules for ${gnome.title}...`}
                  className="w-full h-32 p-3 rounded-xl border border-charcoal-200 dark:border-charcoal-700 bg-charcoal-50 dark:bg-charcoal-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none text-sm"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 p-4 border-t border-charcoal-100 dark:border-charcoal-700 bg-charcoal-50 dark:bg-charcoal-800/50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-charcoal-600 dark:text-charcoal-400 hover:text-charcoal-800 dark:hover:text-charcoal-200 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-400 disabled:opacity-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className={`px-6 py-2 rounded-lg text-sm font-bold text-white shadow-sm flex items-center gap-2 transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-charcoal-800 ${
              saving || loading ? 'bg-charcoal-400 cursor-not-allowed opacity-70' : 'bg-charcoal-900 hover:bg-charcoal-800 dark:bg-white dark:text-charcoal-900 dark:hover:bg-charcoal-200'
            }`}
            aria-busy={saving}
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true" />
                Saving...
              </>
            ) : (
              <>
                <FiCheck aria-hidden="true" /> Save Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GnomeSettingsModal;
